import React, { useState, useEffect, useCallback } from 'react';
import { Check, X, ExternalLink, Loader2, AlertTriangle, Clock } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { apiHeaders } from '../utils/api';
import { usePlan } from '../App';

const API = 'https://aglomera.com/api';

interface SubInfo {
  has_subscription: boolean;
  plan: string;
  billing?: 'mensuel' | 'annuel';
  status?: string;
  cancel_at_period_end?: boolean;
  current_period_end?: number;
  amount?: number;
  interval?: string;
}

interface ProfilInfo {
  plan: string;
  is_trial_active?: boolean;
  trial_premium_until?: string | null;
  trial_used?: boolean;
}

const fmtDate = (ts: number | string) => {
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const fmtAmount = (cents: number) =>
  (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

/** Majuscules pour les lettres uniquement (conserve _ - etc.) */
const normalizeTrialCode = (value: string) =>
  value.replace(/[a-z]/g, c => c.toUpperCase());

const getTrialRemaining = (untilIso: string) => {
  const diff = new Date(untilIso).getTime() - Date.now();
  if (diff <= 0) return null;
  const totalHours = Math.floor(diff / (1000 * 60 * 60));
  return { days: Math.floor(totalHours / 24), hours: totalHours % 24 };
};

const Abonnement: React.FC = () => {
  const { plan, setPlan } = usePlan();
  const location = useLocation();
  const navigate = useNavigate();
  const [annuel, setAnnuel] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [subInfo, setSubInfo] = useState<SubInfo | null>(null);
  const [profilInfo, setProfilInfo] = useState<ProfilInfo | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [trialCode, setTrialCode] = useState('');
  const [trialError, setTrialError] = useState('');
  const [trialLoading, setTrialLoading] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [countdown, setCountdown] = useState<{ days: number; hours: number } | null>(null);

  const prixPro     = annuel ? '7,49 €' : '9,99 €';
  const prixPremium = annuel ? '14,99 €' : '19,99 €';
  const billingPro     = annuel ? 'Facturé 89,90 €/an (−25%)' : 'Facturé mensuellement';
  const billingPremium = annuel ? 'Facturé 179,90 €/an (−25%)' : 'Facturé mensuellement';

  const isTrialActive = Boolean(profilInfo?.is_trial_active);
  const hasStripeSub = Boolean(subInfo?.has_subscription);

  const refreshProfil = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return null;
    const res = await fetch(`${API}/profil/`, { headers: await apiHeaders() });
    if (!res.ok) return null;
    const d = await res.json();
    setProfilInfo(d);
    if (d?.plan) setPlan(d.plan);
    return d as ProfilInfo;
  }, [setPlan]);

  // Retour Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('checkout') === 'success') {
      setToast({ type: 'success', msg: '🎉 Paiement réussi ! Votre plan est en cours d\'activation.' });
      window.history.replaceState({}, '', '/app/abonnement');
      refreshProfil();
      (async () => {
        const hdrs = await apiHeaders();
        fetch(`${API}/stripe/subscription/`, { headers: hdrs })
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d) setSubInfo(d); })
          .catch(() => {});
      })();
    } else if (params.get('checkout') === 'cancel') {
      setToast({ type: 'error', msg: 'Paiement annulé.' });
      window.history.replaceState({}, '', '/app/abonnement');
    }
  }, [location.search, refreshProfil]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  // Chargement initial
  useEffect(() => {
    const load = async () => {
      const user = auth.currentUser;
      if (!user) { setPageLoading(false); return; }
      try {
        await refreshProfil();
        const res = await fetch(`${API}/stripe/subscription/`, { headers: await apiHeaders() });
        if (res.ok) setSubInfo(await res.json());
      } catch { /* silencieux */ }
      finally { setPageLoading(false); }
    };
    load();
  }, [refreshProfil]);

  // Décompte essai (jours + heures)
  useEffect(() => {
    const until = profilInfo?.trial_premium_until;
    if (!isTrialActive || !until) {
      setCountdown(null);
      return;
    }
    const tick = () => setCountdown(getTrialRemaining(until));
    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [isTrialActive, profilInfo?.trial_premium_until]);

  const handleActivateTrial = async (e: React.FormEvent) => {
    e.preventDefault();
    setTrialError('');
    const code = trialCode.trim();
    if (!code) {
      setTrialError('Veuillez saisir un code.');
      return;
    }
    setTrialLoading(true);
    try {
      const res = await fetch(`${API}/trial/activate/`, {
        method: 'POST',
        headers: await apiHeaders(),
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (res.ok) {
        setProfilInfo(data);
        setPlan(data.plan);
        setTrialCode('');
        setShowTrialModal(true);
      } else {
        setTrialError(data.error || 'Code invalide.');
      }
    } catch {
      setTrialError('Erreur réseau. Réessayez.');
    } finally {
      setTrialLoading(false);
    }
  };

  const handleUpgrade = async (target: 'pro' | 'premium') => {
    if (subInfo?.has_subscription) return handlePortal();
    if (!auth.currentUser) return;
    setLoading(target);
    try {
      const res = await fetch(`${API}/stripe/checkout/`, {
        method: 'POST',
        headers: await apiHeaders(),
        body: JSON.stringify({ plan: target, billing: annuel ? 'annuel' : 'mensuel' }),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        setToast({ type: 'error', msg: data.error || 'Erreur lors de la création du paiement.' });
      }
    } catch {
      setToast({ type: 'error', msg: 'Erreur réseau. Réessayez.' });
    } finally {
      setLoading(null);
    }
  };

  const handlePortal = async () => {
    if (!auth.currentUser) return;
    setLoading('portal');
    try {
      const res = await fetch(`${API}/stripe/portal/`, {
        method: 'POST',
        headers: await apiHeaders(),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        setToast({ type: 'error', msg: data.error || 'Erreur portail.' });
      }
    } catch {
      setToast({ type: 'error', msg: 'Erreur réseau. Réessayez.' });
    } finally {
      setLoading(null);
    }
  };

  const isPayingPlan = plan === 'pro' || plan === 'premium';
  const showTrialForm = plan === 'gratuit' && !profilInfo?.trial_used;

  const planLabel = isTrialActive
    ? 'Plan Premium · Essai gratuit'
    : plan === 'gratuit'
      ? 'Plan Gratuit'
      : plan === 'pro'
        ? 'Plan Pro'
        : 'Plan Premium';

  return (
    <div className="p-8 w-full">

      {/* Toast */}
      {toast && (
        <div
          className="fixed top-5 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl text-sm font-semibold shadow-lg transition-all"
          style={{ background: toast.type === 'success' ? '#dcfce7' : '#fee2e2', color: toast.type === 'success' ? '#15803d' : '#b91c1c' }}
        >
          {toast.msg}
        </div>
      )}

      {/* Modal essai activé */}
      {showTrialModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(10,10,10,0.45)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowTrialModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-center w-14 h-14 rounded-2xl mx-auto mb-5"
              style={{ background: '#eef2fc' }}>
              <Check size={24} style={{ color: '#2a52a8' }} />
            </div>
            <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
              Essai Premium de 30 jours activé
            </h2>
            <p className="text-sm text-gray-500 text-center leading-relaxed mb-7">
              Votre accès Premium complet est maintenant actif. Profitez de toutes les offres,
              des 3 newsletters et de l&apos;envoi quotidien — sans carte bancaire.
            </p>
            <button
              type="button"
              onClick={() => { setShowTrialModal(false); navigate('/alertes'); }}
              className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
              style={{ background: '#2a52a8' }}
            >
              Découvrir mes offres
            </button>
          </div>
        </div>
      )}

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Abonnement</h1>
        <p className="text-sm text-gray-500 mt-1">Gérez votre plan Aglomera</p>
      </div>

      {/* ── Section Plan actuel ───────────────────────────────────────────── */}
      <div className="mb-8 max-w-5xl mx-auto rounded-2xl border p-6"
        style={{ background: 'white', borderColor: '#e5e7eb' }}>

        {pageLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Loader2 size={14} className="animate-spin" /> Chargement…
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: plan === 'gratuit' && !isTrialActive ? '#f3f4f6' : '#eef2fc' }}>
                  {plan === 'gratuit' && !isTrialActive ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2a52a8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  )}
                </div>

                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-gray-900">{planLabel}</span>
                    {isTrialActive && (
                      <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: '#eef2fc', color: '#2a52a8' }}>
                        <Clock size={11} /> Essai gratuit
                      </span>
                    )}
                    {hasStripeSub && (() => {
                      const st = subInfo!.status;
                      if (st === 'past_due') return (
                        <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#fef2f2', color: '#b91c1c' }}>
                          <AlertTriangle size={11} /> Paiement échoué
                        </span>
                      );
                      if (subInfo!.cancel_at_period_end) return (
                        <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#fffbeb', color: '#92400e' }}>
                          <Clock size={11} /> Annulation prévue
                        </span>
                      );
                      if (st === 'active') return (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#dcfce7', color: '#15803d' }}>
                          Actif
                        </span>
                      );
                      return null;
                    })()}
                  </div>

                  {isTrialActive && profilInfo?.trial_premium_until && countdown && (
                    <p className="mt-1 text-sm text-gray-500">
                      Il reste{' '}
                      <span className="font-semibold" style={{ color: '#2a52a8' }}>
                        {countdown.days} jour{countdown.days !== 1 ? 's' : ''} et {countdown.hours} heure{countdown.hours !== 1 ? 's' : ''}
                      </span>
                      {' '}· fin le {fmtDate(profilInfo.trial_premium_until)}
                    </p>
                  )}

                  {hasStripeSub ? (
                    <div className="mt-1 text-sm text-gray-500 space-y-0.5">
                      {subInfo!.amount !== undefined && (
                        <p>
                          {fmtAmount(subInfo!.amount!)}/{subInfo!.interval === 'year' ? 'an' : 'mois'}
                          <span className="mx-1.5 text-gray-300">·</span>
                          {subInfo!.billing === 'annuel' ? 'Facturation annuelle' : 'Facturation mensuelle'}
                        </p>
                      )}
                      {subInfo!.current_period_end && (
                        <p>
                          {subInfo!.cancel_at_period_end
                            ? <span style={{ color: '#92400e' }}>Accès jusqu&apos;au {fmtDate(subInfo!.current_period_end)}</span>
                            : <>Prochain renouvellement le {fmtDate(subInfo!.current_period_end)}</>
                          }
                        </p>
                      )}
                    </div>
                  ) : !isTrialActive && plan === 'gratuit' ? (
                    <p className="mt-1 text-sm text-gray-500">
                      Accès limité · Passez au Pro ou Premium pour tout débloquer
                    </p>
                  ) : !isTrialActive && isPayingPlan ? (
                    <p className="mt-1 text-sm text-gray-500">Abonnement actif</p>
                  ) : null}
                </div>
              </div>

              {hasStripeSub && (
                <button
                  onClick={handlePortal}
                  disabled={loading === 'portal'}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all hover:bg-gray-50 disabled:opacity-60 flex-shrink-0"
                  style={{ borderColor: '#2a52a8', color: '#2a52a8' }}
                >
                  {loading === 'portal'
                    ? <Loader2 size={14} className="animate-spin" />
                    : <ExternalLink size={14} />
                  }
                  Gérer mon abonnement
                </button>
              )}
            </div>

            {/* Essai gratuit — code promo */}
            {showTrialForm && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-gray-400 font-medium">ou</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <form onSubmit={handleActivateTrial} className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    value={trialCode}
                    onChange={e => { setTrialCode(normalizeTrialCode(e.target.value)); setTrialError(''); }}
                    placeholder="Code d'essai gratuit"
                    disabled={trialLoading}
                    className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-blue-400 transition-colors disabled:opacity-50"
                    style={{ background: '#fafafa' }}
                  />
                  <button
                    type="submit"
                    disabled={trialLoading || !trialCode.trim()}
                    className="px-6 py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-50 flex-shrink-0"
                    style={{ background: '#2a52a8' }}
                  >
                    {trialLoading ? <Loader2 size={16} className="animate-spin" /> : 'Activer'}
                  </button>
                </form>
                {trialError && (
                  <p className="mt-2 text-sm" style={{ color: '#dc2626' }}>{trialError}</p>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Toggle mensuel / annuel */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex items-center gap-1 p-1 rounded-full" style={{ background: '#f3f4f6', border: '1px solid #e5e7eb' }}>
          <button
            onClick={() => setAnnuel(false)}
            className="px-4 py-1.5 rounded-full text-sm font-semibold transition-all"
            style={!annuel ? { background: '#2a52a8', color: 'white' } : { background: 'transparent', color: '#6b7280' }}
          >
            Mensuel
          </button>
          <button
            onClick={() => setAnnuel(true)}
            className="px-4 py-1.5 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5"
            style={annuel ? { background: '#2a52a8', color: 'white' } : { background: 'transparent', color: '#6b7280' }}
          >
            Annuel
            <span className="text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#dcfce7', color: '#16a34a' }}>−25%</span>
          </button>
        </div>
      </div>

      {/* Cartes tarifaires — inchangées */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch max-w-5xl mx-auto">

        {/* GRATUIT */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 flex flex-col">
          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#6b7280' }}>Gratuit</p>
          <div className="flex items-end gap-1 mb-1">
            <span className="text-3xl font-bold" style={{ color: '#0a0a0a' }}>0 €</span>
          </div>
          <p className="text-sm text-gray-400 mb-6">Pour toujours</p>
          <ul className="flex flex-col gap-2.5 mb-6">
            <li className="flex items-center gap-2 text-sm text-gray-700">
              <Check size={14} style={{ color: '#2a52a8' }} className="flex-shrink-0" />1 newsletter thématique
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-700">
              <Check size={14} style={{ color: '#2a52a8' }} className="flex-shrink-0" />~5 offres sélectionnées par envoi
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-700">
              <Check size={14} style={{ color: '#2a52a8' }} className="flex-shrink-0" />Alerte 2 fois par semaine
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-400 line-through">
              <X size={14} style={{ color: '#d1d5db' }} className="flex-shrink-0" />Toutes les offres disponibles
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-400 line-through">
              <X size={14} style={{ color: '#d1d5db' }} className="flex-shrink-0" />Envoi quotidien
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-400 line-through">
              <X size={14} style={{ color: '#d1d5db' }} className="flex-shrink-0" />3 newsletters thématiques
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-400 line-through">
              <X size={14} style={{ color: '#d1d5db' }} className="flex-shrink-0" />Support prioritaire
            </li>
          </ul>
          <div className="flex-1" />
          {plan === 'gratuit' && !isTrialActive ? (
            <button disabled className="w-full py-2.5 rounded-xl text-sm font-semibold"
              style={{ background: '#f3f4f6', color: '#9ca3af', cursor: 'default' }}>
              Plan actuel
            </button>
          ) : (
            <div className="w-full py-2.5 rounded-xl text-sm font-semibold text-center"
              style={{ background: '#f3f4f6', color: '#d1d5db', cursor: 'default' }}>—</div>
          )}
        </div>

        {/* PRO */}
        <div className="rounded-2xl p-6 flex flex-col relative shadow-md" style={{ background: '#2a52a8', border: '2px solid #2a52a8' }}>
          <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap"
            style={{ background: '#0a0a0a', color: 'white' }}>
            ⭐ Le plus populaire
          </div>
          <div className="flex items-center gap-2 mb-3">
            <p className="text-xs font-bold uppercase tracking-widest text-blue-200">Pro</p>
            {hasStripeSub && plan === 'pro' && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.25)', color: 'white' }}>Actuel</span>
            )}
          </div>
          <div className="flex items-end gap-1 mb-1">
            <span className="text-3xl font-bold text-white">{prixPro}</span>
            <span className="text-blue-200 text-sm mb-1">/mois</span>
          </div>
          <p className="text-sm text-blue-200 mb-6">{billingPro}</p>
          <ul className="flex flex-col gap-2.5 mb-6">
            <li className="flex items-center gap-2 text-sm text-white"><Check size={14} color="white" className="flex-shrink-0" />1 newsletter thématique</li>
            <li className="flex items-center gap-2 text-sm text-white"><Check size={14} color="white" className="flex-shrink-0" />Toutes les offres disponibles</li>
            <li className="flex items-center gap-2 text-sm text-white"><Check size={14} color="white" className="flex-shrink-0" />Envoi quotidien</li>
            <li className="flex items-center gap-2 text-sm text-blue-300 line-through"><X size={14} color="#93c5fd" className="flex-shrink-0" />3 newsletters thématiques</li>
            <li className="flex items-center gap-2 text-sm text-blue-300 line-through"><X size={14} color="#93c5fd" className="flex-shrink-0" />Support prioritaire</li>
          </ul>
          <div className="flex-1" />
          {hasStripeSub ? (
            <button onClick={handlePortal} disabled={!!loading}
              className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-70"
              style={{ background: 'white', color: '#2a52a8' }}>
              {loading === 'portal' && <Loader2 size={14} className="animate-spin" />}
              {plan === 'pro' ? 'Gérer ce plan' : 'Choisir ce plan'}
            </button>
          ) : (
            <button onClick={() => handleUpgrade('pro')} disabled={!!loading}
              className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-70"
              style={{ background: 'white', color: '#2a52a8' }}>
              {loading === 'pro' && <Loader2 size={14} className="animate-spin" />}
              Passer au Pro
            </button>
          )}
        </div>

        {/* PREMIUM */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#6b7280' }}>Premium</p>
            {(hasStripeSub && plan === 'premium') || isTrialActive ? (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#dcfce7', color: '#15803d' }}>Actuel</span>
            ) : null}
          </div>
          <div className="flex items-end gap-1 mb-1">
            <span className="text-3xl font-bold" style={{ color: '#0a0a0a' }}>{prixPremium}</span>
            <span className="text-gray-400 text-sm mb-1">/mois</span>
          </div>
          <p className="text-sm text-gray-400 mb-6">{billingPremium}</p>
          <ul className="flex flex-col gap-2.5 mb-6">
            <li className="flex items-center gap-2 text-sm text-gray-700"><Check size={14} style={{ color: '#2a52a8' }} className="flex-shrink-0" />3 newsletters thématiques</li>
            <li className="flex items-center gap-2 text-sm text-gray-700"><Check size={14} style={{ color: '#2a52a8' }} className="flex-shrink-0" />Toutes les offres disponibles</li>
            <li className="flex items-center gap-2 text-sm text-gray-700"><Check size={14} style={{ color: '#2a52a8' }} className="flex-shrink-0" />Envoi quotidien</li>
            <li className="flex items-center gap-2 text-sm text-gray-700"><Check size={14} style={{ color: '#2a52a8' }} className="flex-shrink-0" />Support prioritaire</li>
          </ul>
          <div className="flex-1" />
          {isTrialActive ? (
            <button disabled className="w-full py-2.5 rounded-xl text-sm font-semibold"
              style={{ background: '#eef2fc', color: '#2a52a8', cursor: 'default' }}>
              Essai en cours
            </button>
          ) : hasStripeSub ? (
            <button onClick={handlePortal} disabled={!!loading}
              className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all text-white flex items-center justify-center gap-2 disabled:opacity-70"
              style={{ background: '#2a52a8' }}>
              {loading === 'portal' && <Loader2 size={14} className="animate-spin" />}
              {plan === 'premium' ? 'Gérer ce plan' : 'Choisir ce plan'}
            </button>
          ) : (
            <button onClick={() => handleUpgrade('premium')} disabled={!!loading}
              className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all text-white flex items-center justify-center gap-2 disabled:opacity-70"
              style={{ background: '#2a52a8' }}>
              {loading === 'premium' && <Loader2 size={14} className="animate-spin" />}
              Passer au Premium
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Abonnement;
